'use client';

import { useDebug } from '@shared/debug';
import { useIsMobile } from '@shared/utils';
import { useTimeZone, useTranslations } from 'next-intl';
import { useEffect, useEffectEvent, useMemo, useState, useSyncExternalStore } from 'react';
import type { SelectOption } from '../../../form-controls/types';
import type { LotteryName } from '../../../lottery-purchases/types';
import type { Drawing, DrawingDatesQueryParams, DrawingDatesResponse, DrawingLotteryType } from '../../types';
import { buildResultsRangeParams, expandDrawingDateRanges, getDrawingDateParts, padDatePart } from '../../utils';
import { useDrawingDates } from '../useDrawingDates/useDrawingDates';
import { useResultsByRange } from '../useResultsByRange/useResultsByRange';

type DrawHistorySelection = {
  year?: number;
  month?: number;
  day?: number;
  hour?: number;
};

type DrawHistoryDropdownKey = 'year' | 'month' | 'day' | 'hour' | 'result';

type DrawHistoryDropdown = {
  key: DrawHistoryDropdownKey;
  id: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
};

const subscribeToHydration = () => () => {};
const getClientHydrationSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

function useIsClientTimeZoneReady() {
  return useSyncExternalStore(subscribeToHydration, getClientHydrationSnapshot, getServerHydrationSnapshot);
}

function sortDrawingsDescending(data: Drawing[]): Drawing[] {
  return [...data].sort((aa, bb) => new Date(bb.drawingDate).getTime() - new Date(aa.drawingDate).getTime());
}

function toOption(value: number, text: string): SelectOption {
  return { value: String(value), text };
}

function getParentDropdownKeys(lotteryType: DrawingLotteryType): Exclude<DrawHistoryDropdownKey, 'result'>[] {
  if (lotteryType === 'NORMAL') return ['year', 'month'];
  if (lotteryType === 'GO') return ['year', 'month', 'day'];
  return ['year', 'month', 'day', 'hour'];
}

function syncSelectionFromDates(dates: DrawingDatesResponse): DrawHistorySelection {
  return {
    year: dates.year,
    month: dates.month,
    day: dates.day,
    hour: dates.hour,
  };
}

/**
 * Orchestrates archive date dropdowns and range results for the draw-history page.
 * Parent dropdown options come from `/dates`; the last dropdown is derived from `/range` results.
 * Returns the underlying query results so consumers can wrap them in `QueriesBoundary`.
 */
function useDrawHistory(lotteryName: LotteryName) {
  const timeZone = useTimeZone();
  const isClientTimeZoneReady = useIsClientTimeZoneReady();
  const isMobile = useIsMobile({ breakpoint: 'sm' });
  const t = useTranslations('lotteries.results.draw_history_filters');
  const debug = useDebug();

  const [selectionOverride, setSelectionOverride] = useState<{ source: DrawingDatesResponse; value: DrawHistorySelection } | null>(null);
  const [datesFilter, setDatesFilter] = useState<DrawingDatesQueryParams>({});
  const [pickedResult, setPickedResult] = useState<{ source: Drawing[]; drawingDate: string } | null>(null);

  const datesParams = {
    ...datesFilter,
    timezone: timeZone,
  };

  const datesQuery = useDrawingDates(lotteryName, { params: datesParams, enabled: isClientTimeZoneReady });
  const { data: dates } = datesQuery;
  const lotteryType = dates?.lotteryType;
  const selection: DrawHistorySelection = dates && selectionOverride?.source === dates ? selectionOverride.value : dates ? syncSelectionFromDates(dates) : {};

  const updateSelection = (patch: DrawHistorySelection) => {
    if (!dates) return;
    setSelectionOverride({ source: dates, value: { ...selection, ...patch } });
  };

  const rangeParams =
    lotteryType && selection.year !== undefined && selection.month !== undefined
      ? buildResultsRangeParams(
          lotteryType,
          {
            year: selection.year,
            month: selection.month,
            day: selection.day,
            hour: selection.hour,
          },
          timeZone
        )
      : undefined;

  const resultsQuery = useResultsByRange(lotteryName, {
    params: rangeParams ?? { from: '', to: '' },
    enabled: isClientTimeZoneReady && Boolean(rangeParams),
  });
  const { data: results, isPlaceholderData: isPlaceholderResults } = resultsQuery;

  const reportEmptyRange = useEffectEvent(() => {
    debug.forceError(`[useDrawHistory] /range returned no draws for ${lotteryName}`, { rangeParams, selection, dates });
  });

  useEffect(() => {
    if (!results || isPlaceholderResults || results.length > 0) return;
    reportEmptyRange();
  }, [results, isPlaceholderResults]);

  const sortedResults = useMemo(() => sortDrawingsDescending(results ?? []), [results]);

  const currentResult =
    (pickedResult?.source === sortedResults ? sortedResults.find(drawing => drawing.drawingDate === pickedResult.drawingDate) : undefined) ?? sortedResults[0] ?? null;

  const yearOptions = expandDrawingDateRanges(dates?.years, { descending: true }).map(year => toOption(year, String(year)));

  const monthOptions = expandDrawingDateRanges(dates?.months).map(month => {
    const date = new Date(Date.UTC(selection.year ?? dates?.year ?? 2000, month - 1, 15, 12));
    return toOption(month, t(isMobile ? 'month_short' : 'month', { date }));
  });

  const dayOptions = expandDrawingDateRanges(dates?.days).map(day => toOption(day, t('day', { day })));

  const hourOptions = expandDrawingDateRanges(dates?.hours).map(hour => toOption(hour, t('hour', { time: `${padDatePart(hour)}:00` })));

  const resultOptions: SelectOption[] = !lotteryType
    ? []
    : [...sortedResults].reverse().map(drawing => {
        const parts = getDrawingDateParts(drawing.drawingDate, timeZone);

        if (lotteryType === 'NORMAL') {
          return { value: drawing.drawingDate, text: t('day', { day: parts.day }) };
        }
        if (lotteryType === 'GO') {
          return { value: drawing.drawingDate, text: t('hour', { time: `${padDatePart(parts.hour)}:00` }) };
        }

        return {
          value: drawing.drawingDate,
          text: t('minute', { time: `${padDatePart(parts.hour)}:${padDatePart(parts.minute)}` }),
        };
      });

  const handleYearChange = (value: string) => {
    setDatesFilter({ year: Number(value) });
  };

  const handleMonthChange = (value: string) => {
    const month = Number(value);
    if (!lotteryType || lotteryType === 'NORMAL') {
      updateSelection({ month, day: undefined, hour: undefined });
      return;
    }
    if (selection.year === undefined) return;
    setDatesFilter({ year: selection.year, month });
  };

  const handleDayChange = (value: string) => {
    const day = Number(value);
    if (lotteryType === 'GO') {
      updateSelection({ day, hour: undefined });
      return;
    }
    if (selection.year === undefined || selection.month === undefined) return;
    setDatesFilter({ year: selection.year, month: selection.month, day });
  };

  const handleHourChange = (value: string) => {
    updateSelection({ hour: Number(value) });
  };

  const handleResultChange = (drawingDate: string) => {
    if (!sortedResults.some(drawing => drawing.drawingDate === drawingDate)) return;
    setPickedResult({ source: sortedResults, drawingDate });
  };

  const dropdowns: DrawHistoryDropdown[] = [];

  if (lotteryType) {
    const parentKeys = getParentDropdownKeys(lotteryType);

    if (parentKeys.includes('year')) {
      dropdowns.push({
        key: 'year',
        id: 'draw-history-year',
        options: yearOptions,
        value: selection.year !== undefined ? String(selection.year) : '',
        onChange: handleYearChange,
      });
    }
    if (parentKeys.includes('month')) {
      dropdowns.push({
        key: 'month',
        id: 'draw-history-month',
        options: monthOptions,
        value: selection.month !== undefined ? String(selection.month) : '',
        onChange: handleMonthChange,
      });
    }
    if (parentKeys.includes('day')) {
      dropdowns.push({
        key: 'day',
        id: 'draw-history-day',
        options: dayOptions,
        value: selection.day !== undefined ? String(selection.day) : '',
        onChange: handleDayChange,
      });
    }
    if (parentKeys.includes('hour')) {
      dropdowns.push({
        key: 'hour',
        id: 'draw-history-hour',
        options: hourOptions,
        value: selection.hour !== undefined ? String(selection.hour) : '',
        onChange: handleHourChange,
      });
    }

    dropdowns.push({
      key: 'result',
      id: 'draw-history-result',
      options: resultOptions,
      value: currentResult?.drawingDate ?? '',
      onChange: handleResultChange,
    });
  }

  return {
    lotteryType,
    currentResult,
    dropdowns,
    datesQuery,
    resultsQuery,
  };
}

export { getParentDropdownKeys, useDrawHistory };
export type { DrawHistoryDropdown, DrawHistoryDropdownKey, DrawHistorySelection };
